import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { Home } from 'lucide-react';
import HomeScreen from './HomeScreen';
import QuestionScreen from './QuestionScreen';
import ResultScreen from './ResultScreen';
import StaffPanel from './StaffPanel';
import { QUESTIONS } from './data';

// Auto-return to home (idle attractor) after this many ms with no interaction.
const IDLE_TIMEOUT_MS = 60_000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Evaluate whether a question applies given current answers.
const isVisible = (q, answers) => {
  if (!q.dependsOn) return true;
  return Object.entries(q.dependsOn).every(([k, v]) => answers[k] === v);
};

const KioskApp = () => {
  const [stage, setStage] = useState('home');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [staffOpen, setStaffOpen] = useState(false);
  const [medOptions, setMedOptions] = useState([]);

  // Load medication options once (for the dynamic multi-select).
  useEffect(() => {
    axios.get(`${API}/kiosk/medications`)
      .then((res) => setMedOptions(res.data.medications || []))
      .catch(() => setMedOptions([]));
  }, []);

  // Auto-return to home (idle attractor) after IDLE_TIMEOUT_MS of no interaction.
  // Skip while we're already on home (no need to fire).
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (stage === 'home') return undefined;

    const resetToHome = () => {
      setStage('home');
      setAnswers({});
      setStepIndex(0);
    };

    const arm = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(resetToHome, IDLE_TIMEOUT_MS);
    };

    arm();
    const events = ['pointerdown', 'touchstart', 'keydown', 'mousemove'];
    events.forEach((ev) => window.addEventListener(ev, arm, { passive: true }));

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, arm));
    };
  }, [stage]);

  // Inject dynamic options into the `medications` question.
  const questions = useMemo(() => QUESTIONS.map((q) => {
    if (q.id === 'medications') {
      return { ...q, options: medOptions.map(m => ({ value: m.id, label: m.label })) };
    }
    return q;
  }), [medOptions]);

  // Visible question list based on current answers (skips irrelevant Qs).
  const visibleQuestions = useMemo(
    () => questions.filter((q) => isVisible(q, answers)),
    [questions, answers],
  );

  const tapsRef = useRef([]);
  const onCornerTap = () => {
    const now = Date.now();
    tapsRef.current = tapsRef.current.filter((t) => now - t < 3000);
    tapsRef.current.push(now);
    if (tapsRef.current.length >= 5) {
      tapsRef.current = [];
      setStaffOpen(true);
    }
  };

  const startAssessment = () => {
    setAnswers({});
    setStepIndex(0);
    setStage('questions');
    // Fire-and-forget analytics ping. Failure is non-fatal — never blocks UX.
    axios.post(`${API}/kiosk/start`).catch(() => {});
  };

  // Advance helper — uses the latest answers to decide what's next.
  const advance = (nextAnswers) => {
    const next = questions.filter((q) => isVisible(q, nextAnswers));
    // Re-find the current question's position in the newly-visible list, then +1.
    const current = visibleQuestions[stepIndex];
    let nextIndex = next.findIndex((q) => q.id === current?.id) + 1;
    if (nextIndex <= 0 || nextIndex >= next.length) {
      setStage('result');
    } else {
      // Map back to source index (array order of `questions`) is not needed —
      // we drive by the visible list. Since `visibleQuestions` is recomputed
      // from the latest `answers` state, just set stepIndex to the new slot.
      setStepIndex(nextIndex);
    }
  };

  const handleAnswer = (qid, value) => {
    const nextAnswers = { ...answers, [qid]: value };
    setAnswers(nextAnswers);
    // Auto-advance for single-select.
    setTimeout(() => advance(nextAnswers), 220);
  };

  // Multi-select: user taps options to toggle; "Continue" button advances.
  const handleMultiToggle = (qid, value) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[qid]) ? prev[qid] : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [qid]: next };
    });
  };

  const handleMultiContinue = (qid) => {
    // Ensure key exists (empty array is fine).
    const nextAnswers = { ...answers, [qid]: Array.isArray(answers[qid]) ? answers[qid] : [] };
    setAnswers(nextAnswers);
    advance(nextAnswers);
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      setStage('home');
    } else {
      setStepIndex((i) => i - 1);
    }
  };

  const restart = () => {
    setAnswers({});
    setStepIndex(0);
    setStage('home');
  };

  const screenVariants = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };
  const screenTransition = { type: 'spring', stiffness: 200, damping: 26 };
  const currentQ = visibleQuestions[stepIndex];
  const screenKey =
    stage === 'questions' ? `q-${currentQ?.id || stepIndex}` : stage;

  return (
    <div
      className="min-h-screen flex flex-col text-[#0d1f15] select-none kiosk-root"
      style={{
        backgroundColor: '#F6F4F0',
        backgroundImage:
          'radial-gradient(60rem 40rem at 0% -10%, rgba(31,61,43,0.08), transparent 60%),' +
          ' radial-gradient(50rem 35rem at 100% 110%, rgba(217,119,54,0.07), transparent 65%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.04] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <button
        data-testid="kiosk-staff-hotspot"
        onClick={onCornerTap}
        aria-label="Staff mode"
        className="fixed top-0 right-0 w-16 h-16 z-[60] opacity-0"
      />

      {stage !== 'home' && (
        <header className="relative z-10 px-6 pt-6 pb-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              data-testid="kiosk-home-button"
              onClick={() => { window.location.href = '/'; }}
              aria-label="Return to launcher"
              className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/70 backdrop-blur border border-[#1f3d2b]/15 flex items-center justify-center text-[#1f3d2b] active:bg-[#1f3d2b]/5 transition-colors"
            >
              <Home size={20} />
            </button>
            <img
              src="/logo.png"
              alt="Healthy Life Vitamins"
              className="h-12 sm:h-14 w-auto object-contain drop-shadow-sm"
            />
            <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14" />
          </div>
        </header>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={screenKey}
          variants={screenVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={screenTransition}
          className="flex-1 flex flex-col relative z-10"
        >
          {stage === 'home' && <HomeScreen onStart={startAssessment} />}
          {stage === 'questions' && currentQ && (
            <QuestionScreen
              question={currentQ}
              stepIndex={stepIndex}
              totalSteps={visibleQuestions.length}
              answers={answers}
              onAnswer={handleAnswer}
              onMultiToggle={handleMultiToggle}
              onMultiContinue={handleMultiContinue}
              onBack={handleBack}
            />
          )}
          {stage === 'result' && (
            <ResultScreen answers={answers} onRestart={restart} />
          )}
        </motion.div>
      </AnimatePresence>

      {staffOpen && <StaffPanel onClose={() => setStaffOpen(false)} />}

      <footer className="relative z-10 text-center py-6 text-[10px] uppercase tracking-[0.25em] text-[#5a7062]">
        Healthy Life Vitamins · Wellness Club
      </footer>
    </div>
  );
};

export default KioskApp;
